import React from "react";
import { Routes, Route, useLocation } from "react-router-dom";

//importing layouts
import PublicLayout from "./layouts/publicLayout";
import UserLayout from "./user/userLayout";

//import Auth Guards
import UserAuthGuard from "./Auth/UserAuthGuard";
import AdminAuthGuard from "./Auth/AdminAuthGuard";
import ProtectedLoginRoute from "./Auth/ProtectedLoginRoute";

// Importing components and pages
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";

import Home from "./pages/Home";
import AboutPage from "./pages/AboutPage";
import PortfolioPage from "./pages/PortfolioPage";
import ContactPage from "./pages/ContactPage";
import RefundStatusPage from "./pages/RefundStatusPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import PaymentPage from "./pages/PaymentPage";
import NotFound from "./pages/NotFound";

import ServicesPage from "./pages/services/ServicesPage";
import Staffing from "./pages/services/Staffing";
import BusinessConsulting from "./pages/services/BusinessConsulting";

import IndianTaxFiling from "./pages/Taxfiling/IndianTaxFiling";
import IndividualTaxFiling from "./pages/Taxfiling/IndividualTaxFiling";
import BusinessTaxFiling from "./pages/Taxfiling/BusinessTaxFiling";
import TaxPreparation from "./pages/Taxfiling/TaxPreparation";
import AdvanceTaxPlanning from "./pages/Taxfiling/AdvanceTaxPlanning";
import FbarFactaFiling from "./pages/Taxfiling/FbarFactaFiling";
import ItinAssistance from "./pages/Taxfiling/ItinAssistance";
import FdrlStateNoticeResolution from "./pages/Taxfiling/FdrlStateNoticeResolution";
import TaxAmendments from "./pages/Taxfiling/TaxAmendments";
import ExpertConsulting from "./pages/Taxfiling/ExpertConsulting";

// SignIn, SignUp and ForgotPassword imports
import SignIn from "./signin/signin";
import SignUp from "./signin/signup";
import ForgotPassword from "./signin/forgotPassword";

// User-related imports
import Dashboard from "./user/dashboard";
import ChangePassword from "./user/changePassword";
import UploadDocuments from "./user/uploadDocuments";
import Amendment from "./user/amendment";
import DownloadTaxInformationForm from "./user/downloadTaxInformationForm";
import DownloadTaxReturnDraftCopy from "./user/downloadTaxReturnDraftCopy";
import DownloadTaxReturnFinalCopy from "./user/downloadTaxReturnFinalCopy";

//admin-related imports
import AdminLogin from "./admin/adminLogin";
import AdminLayout from "./admin/adminLayout";
import AdminDashboard from "./admin/dashboard";
import Category from "./admin/Administrator/category";
import Year from "./admin/Administrator/year";
import CategoryFiles from "./admin/Administrator/categoryFiles";
import Users from "./admin/Users/users";
import UserUploads from "./admin/Users/userUploads";
import AmendmentUploads from "./admin/Users/amendmentUploads";
import UserDetails from "./admin/Users/userDownloads";
import ContactApproaches from "./admin/Users/contactApproaches";
import Employees from "./admin/Employees/employees";

export default function App() {
  const location = useLocation();

  // Determine if current path starts with /user
  const isUserRoute = location.pathname.startsWith("/user");

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public routes under PublicLayout */}
        <Route path="/" element={<PublicLayout />}>
          <Route index element={<Home />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="refund-status" element={<RefundStatusPage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="portfolio" element={<PortfolioPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="payment" element={<PaymentPage />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="services/business-consulting" element={<BusinessConsulting />} />
          <Route path="services/staffing" element={<Staffing />} />
          <Route path="taxfiling/indian-tax-filing" element={<IndianTaxFiling />} />
          <Route path="taxfiling/individual-tax-filing" element={<IndividualTaxFiling />} />
          <Route path="taxfiling/business-tax-filing" element={<BusinessTaxFiling />} />
          <Route path="taxfiling/advance-tax-planning" element={<AdvanceTaxPlanning />} />
          <Route path="taxfiling/tax-preparation" element={<TaxPreparation />} />
          <Route path="taxfiling/fbar-facta-filing" element={<FbarFactaFiling />} />
          <Route path="taxfiling/itin-assistance" element={<ItinAssistance />} />
          <Route path="taxfiling/fdrl-state-notice-resolution" element={<FdrlStateNoticeResolution />} />
          <Route path="taxfiling/tax-amendments" element={<TaxAmendments />} />
          <Route path="taxfiling/expert-consulting" element={<ExpertConsulting />} />
          <Route path="signin" element={<ProtectedLoginRoute userType="user"><SignIn /></ProtectedLoginRoute>} />
          <Route path="signup" element={<ProtectedLoginRoute userType="user"><SignUp /></ProtectedLoginRoute>} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          {/* 404 Not Found - Catch all route for public routes */}
          <Route path="*" element={<NotFound />} />
        </Route>
      {/* User module routes wrapped with PrivateRoute and UserLayout */}
      <Route path="/user" element={<UserAuthGuard><UserLayout /></UserAuthGuard>}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="changepassword" element={<ChangePassword />} />
        <Route path="uploaddocuments" element={<UploadDocuments />} />
        <Route path="downloadform" element={<DownloadTaxInformationForm />} />
        <Route path="downloaddraftcopy" element={<DownloadTaxReturnDraftCopy />} />
        <Route path="downloadfinalcopy" element={<DownloadTaxReturnFinalCopy />} />
        <Route path="amendment" element={<Amendment />} />
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* Admin Auth and Dashboard routes */}
      <Route path="/admin/login" element={<ProtectedLoginRoute userType="admin"><AdminLogin /></ProtectedLoginRoute>} />
      <Route path="/admin" element={<AdminAuthGuard><AdminLayout /></AdminAuthGuard>}>
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="administrator/category" element={<Category />} />
        <Route path="administrator/year" element={<Year />} />
        <Route path="administrator/category-files" element={<CategoryFiles />} />
        <Route path="users/list" element={<Users />} />
        <Route path="users/uploads" element={<UserUploads />} />
        <Route path="users/amendment-uploads" element={<AmendmentUploads />} />
        <Route path="users/details" element={<UserDetails />} />
        <Route path="users/approaches" element={<ContactApproaches />} />
        <Route path="employees" element={<Employees />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
    </>
  );
}
